import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { notificationsRepository } from '../repositories/notifications.repository'
import { useMutationTracker } from '../hooks/useMutationTracker'
import type { NotificationType } from '../models/notification'

const NOTIFICATIONS_KEY = 'notifications'
const UNREAD_COUNT_KEY = 'notifications-unread-count'
const MARK_AS_READ_KEY = 'mark-notification-read'
const MARK_ALL_AS_READ_KEY = 'mark-all-notifications-read'

export const useNotificationsState = () => {
  const queryClient = useQueryClient()

  const {
    data: notificationsData,
    isLoading: dataIsLoading,
    error: dataError,
    refetch: refetchNotifications
  } = useQuery({
    queryKey: [NOTIFICATIONS_KEY],
    queryFn: () => notificationsRepository.getAllNotifications({ limit: 50 }),
    refetchInterval: 30000 // Poll every 30 seconds
  })

  const {
    data: unreadCountData,
    isLoading: unreadCountLoading,
    error: unreadCountError,
    refetch: refetchUnreadCount
  } = useQuery({
    queryKey: [UNREAD_COUNT_KEY],
    queryFn: () => notificationsRepository.getUnreadCount(),
    refetchInterval: 30000 // Poll every 30 seconds
  })

  const markAsReadMutation = useMutation({
    mutationKey: [MARK_AS_READ_KEY],
    mutationFn: (id: number) => notificationsRepository.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY] })
      queryClient.invalidateQueries({ queryKey: [UNREAD_COUNT_KEY] })
    }
  })

  const markAllAsReadMutation = useMutation({
    mutationKey: [MARK_ALL_AS_READ_KEY],
    mutationFn: (type?: NotificationType) => notificationsRepository.markAllAsRead(type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY] })
      queryClient.invalidateQueries({ queryKey: [UNREAD_COUNT_KEY] })
    }
  })

  const markAsReadState = useMutationTracker(MARK_AS_READ_KEY)
  const markAllAsReadState = useMutationTracker(MARK_ALL_AS_READ_KEY)

  return {
    // Data
    notifications: notificationsData?.data || [],
    notificationsMeta: notificationsData?.meta,
    unreadCount: unreadCountData?.data.unread_count || 0,

    // Loading and error states
    dataIsLoading,
    unreadCountLoading,
    dataError,
    unreadCountError,

    // Actions
    markAsRead: markAsReadMutation.mutate,
    markAllAsRead: markAllAsReadMutation.mutate,
    refetchNotifications,
    refetchUnreadCount,

    // Mutation states
    markAsReadLoading: markAsReadState.isPending,
    markAsReadError: markAsReadState.error,
    markAsReadSuccess: markAsReadState.isSuccess,
    markAsReadData: markAsReadState.data,

    markAllAsReadLoading: markAllAsReadState.isPending,
    markAllAsReadError: markAllAsReadState.error,
    markAllAsReadSuccess: markAllAsReadState.isSuccess,
    markAllAsReadData: markAllAsReadState.data
  }
}
